import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { isComplaintTask, assigneeOptionsFor, assigneeOptionLabel, assignmentErrorMessage } from "../../utils/complaintAssignee";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Badge,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { taskDisplayTitle } from "../../utils/taskDisplay";
import "./AdminTasksPage.css";

// Simplified backdated Task Date -- local-date "YYYY-MM-DD", matching a
// native <input type="date">'s own value format. Never uses toISOString()
// (UTC-based -- can roll the calendar date back a day in a timezone east
// of UTC); built from local Date getters instead so "today" always means
// the browser's own local today.
const todayDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Closed/Cancelled removed from selectable options -- drives the filter
// dropdown, the status-change dropdown, AND the dashboard stat cards below
// (all three .map() over this array), so removing them here also removes
// their now-obsolete "active dashboard" cards in one place. They remain
// valid, readable statuses on existing historical tasks (see
// LEGACY_STATUS_LABELS) but are no longer selectable for a new task or a
// status change. Mirrors task-management-backend/utils/status.js's
// isLegacyNonSelectableTaskStatus().
const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const LEGACY_STATUS_LABELS = {
  closed: "Closed (Legacy)",
  cancelled: "Cancelled (Legacy)",
};

// Decorative-only: icon + color tone per selectable status, used to render
// the dashboard stat cards below. Purely presentational -- does not affect
// which statuses are selectable (that's still governed by STATUS_OPTIONS).
const STATUS_STAT_META = {
  pending: { icon: "fa-hourglass-half", tone: "warning" },
  in_progress: { icon: "fa-arrows-rotate", tone: "accent" },
  completed: { icon: "fa-circle-check", tone: "success" },
};

const useDebouncedState = (initial = "", delay = 350) => {
  const [value, setValue] = useState(initial);
  const [debounced, setDebounced] = useState(initial);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return { value, setValue, debounced };
};

export default function AdminTasksPage() {
  const { user } = useContext(AuthContext) || { user: null };
  const navigate = useNavigate();

  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const {
    value: search,
    setValue: setSearch,
    debounced: debouncedSearch,
  } = useDebouncedState("", 350);

  const [statusFilter, setStatusFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    unit_id: "",
    category_id: "",
    sub_category_id: "",
    // Simplified backdated Task Date -- available to every user/role that
    // can already open this Add Task modal at all; no extra permission, no
    // feature flag. Defaults to today.
    task_date: todayDateString(),
  });

  const [showEdit, setShowEdit] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editAssignedTo, setEditAssignedTo] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editNote, setEditNote] = useState("");

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setError("");
      setLoading(true);

      const [unitRes, userRes] = await Promise.all([
        api.get(`/units`),
        api.get(`/users`),
      ]);

      setUnits(Array.isArray(unitRes.data) ? unitRes.data : []);
      setUsers(Array.isArray(userRes.data) ? userRes.data : []);

      await refreshTasks();
    } catch (error) {
      console.error("Initial Load Error:", error);
      setError(error?.response?.data?.error || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const refreshTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const { data } = await api.get(`/tasks`);
      setTasks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Get Tasks Error:", error);
      setError(error?.response?.data?.error || "Failed to fetch tasks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!createForm.unit_id) {
      setCategories([]);
      setSubcategories([]);
      setCreateForm((prev) => ({
        ...prev,
        category_id: "",
        sub_category_id: "",
      }));
      return;
    }

    api
      .get(`/categories/by-unit/${createForm.unit_id}`)
      .then((res) => {
        setCategories(Array.isArray(res.data) ? res.data : []);
        setCreateForm((prev) => ({
          ...prev,
          category_id: "",
          sub_category_id: "",
        }));
      })
      .catch((error) => {
        console.error("Category Load Error:", error);
        setCategories([]);
      });
  }, [createForm.unit_id]);

  useEffect(() => {
    if (!createForm.category_id) {
      setSubcategories([]);
      setCreateForm((prev) => ({
        ...prev,
        sub_category_id: "",
      }));
      return;
    }

    api
      .get(`/subcategories/by-category/${createForm.category_id}`)
      .then((res) => {
        setSubcategories(Array.isArray(res.data) ? res.data : []);
        setCreateForm((prev) => ({
          ...prev,
          sub_category_id: "",
        }));
      })
      .catch((error) => {
        console.error("Subcategory Load Error:", error);
        setSubcategories([]);
      });
  }, [createForm.category_id]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, unitFilter, assigneeFilter, pageSize]);

  const filtered = useMemo(() => {
    const query = debouncedSearch.toLowerCase().trim();

    return tasks.filter((task) => {
      const passSearch =
        !query ||
        [
          task.title,
          task.description,
          task.category_name,
          task.sub_category_name,
          task.status,
          task.assigned_to_name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      const passStatus = !statusFilter || task.status === statusFilter;
      const passUnit = !unitFilter || String(task.unit_id) === String(unitFilter);
      const passAssignee =
        !assigneeFilter || String(task.assigned_to) === String(assigneeFilter);

      return passSearch && passStatus && passUnit && passAssignee;
    });
  }, [tasks, debouncedSearch, statusFilter, unitFilter, assigneeFilter]);

  const sorted = useMemo(() => {
    const data = [...filtered];

    data.sort((a, b) => {
      const direction = sortDir === "asc" ? 1 : -1;

      const valueA = a[sortBy] ?? "";
      const valueB = b[sortBy] ?? "";

      if (sortBy === "created_at" || sortBy === "updated_at") {
        return (new Date(valueA) - new Date(valueB)) * direction;
      }

      return String(valueA).localeCompare(String(valueB)) * direction;
    });

    return data;
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

  const current = useMemo(() => {
    return sorted.slice((page - 1) * pageSize, page * pageSize);
  }, [sorted, page, pageSize]);

  const counts = useMemo(() => {
    const result = {
      total: tasks.length,
    };

    STATUS_OPTIONS.forEach((status) => {
      result[status.value] = tasks.filter((task) => task.status === status.value).length;
    });

    return result;
  }, [tasks]);

  const getStatusLabel = (value) => {
    return (
      STATUS_OPTIONS.find((status) => status.value === value)?.label ||
      LEGACY_STATUS_LABELS[value] ||
      value ||
      "-"
    );
  };

  const StatusBadge = ({ value }) => {
    return (
      <Badge className={`unit-status admintasks-status-${value || "unknown"}`}>
        {getStatusLabel(value)}
      </Badge>
    );
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return "↕";
    return sortDir === "asc" ? "↑" : "↓";
  };

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const serialNumber = (index) => {
    return (page - 1) * pageSize + index + 1;
  };

  const resetCreateForm = () => {
    setCreateForm({
      title: "",
      description: "",
      unit_id: "",
      category_id: "",
      sub_category_id: "",
      task_date: todayDateString(),
    });
  };

  const openCreateModal = () => {
    resetCreateForm();
    setShowCreate(true);
  };

  // Estimated TAT -- read-only display of the Subcategory's own configured
  // tat_value/tat_unit (services/subcategoryTatService.js is the single
  // source of truth). Never editable here and never sent in the payload --
  // the backend independently resolves TAT from sub_category_id alone.
  const selectedSubcategory = subcategories.find(
    (s) => String(s.id) === String(createForm.sub_category_id)
  );
  const subcategoryHasTat = !!(
    selectedSubcategory &&
    selectedSubcategory.tat_value != null &&
    selectedSubcategory.tat_unit
  );

  const onCreate = async () => {
    if (
      !createForm.title ||
      !createForm.unit_id ||
      !createForm.category_id ||
      !createForm.sub_category_id ||
      !createForm.task_date
    ) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      await api.post(`/tasks`, {
        ...createForm,
        created_by: user?.id,
      });

      setShowCreate(false);
      resetCreateForm();
      await refreshTasks();

      alert("Task created successfully");
    } catch (error) {
      console.error("Create Task Error:", error);
      alert(error?.response?.data?.error || error?.response?.data?.message || "Failed to create task.");
    }
  };

  const openEdit = (task) => {
    setEditTask(task);
    setEditAssignedTo(task.assigned_to || "");
    setEditStatus(task.status || "");
    setEditNote("");
    setShowEdit(true);
  };

  const saveEdit = async () => {
    try {
      if (editAssignedTo && editTask) {
        await api.put(`/tasks/assign/${editTask.id}`, {
          assigned_to: Number(editAssignedTo),
        });
      }

      if (editStatus && editTask) {
        await api.put(`/tasks/${editTask.id}/status`, {
          status: editStatus,
          note: editNote || undefined,
          actor_id: user?.id,
        });
      }

      setShowEdit(false);
      await refreshTasks();

      alert("Task updated successfully");
    } catch (error) {
      console.error("Edit Task Error:", error);
      alert(assignmentErrorMessage(error, "Failed to save changes."));
    }
  };

  const openView = (id) => {
    navigate(`/admin/tasks/${id}`);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setUnitFilter("");
    setAssigneeFilter("");
    setPage(1);
  };

  const exportCSV = () => {
    if (!sorted.length) {
      alert("No data available to export");
      return;
    }

    const headers = [
      "ID",
      "Title",
      "Description",
      "Category",
      "Subcategory",
      "Staff",
      "Assigned To",
      "Status",
      "Created At",
    ];

    const rows = sorted.map((task) => [
      task.id,
      taskDisplayTitle(task),
      task.description,
      task.category_name,
      task.sub_category_name,
      task.requested_for_name,
      task.assigned_to_name,
      getStatusLabel(task.status),
      task.created_at,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value || "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "admin-tasks.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="unit-page admintasks-theme">
      <div className="unit-header">
        <div className="admintasks-heading-content">
          <div className="admintasks-heading-icon">
            <i className="fa-solid fa-list-check" aria-hidden="true"></i>
          </div>

          <div>
            <div className="unit-page-label">Task Management</div>
            <h2>Admin Task Dashboard</h2>
            <p>Manage tasks, assignments, status changes, remarks and ticket history.</p>
          </div>
        </div>

        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={refreshTasks}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>

          <Button className="unit-btn unit-btn-light" onClick={exportCSV}>
            <i className="fa-solid fa-file-export" aria-hidden="true"></i>
            Export CSV
          </Button>

          <Button className="unit-btn unit-btn-primary" onClick={openCreateModal}>
            <i className="fa-solid fa-plus" aria-hidden="true"></i>
            Add Task
          </Button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="admintasks-stat-icon admintasks-stat-icon-primary">
            <i className="fa-solid fa-list-check" aria-hidden="true"></i>
          </div>
          <div className="admintasks-stat-text">
            <span>Total</span>
            <strong>{counts.total}</strong>
          </div>
        </div>

        {STATUS_OPTIONS.map((status) => {
          const meta = STATUS_STAT_META[status.value] || { icon: "fa-circle", tone: "primary" };
          return (
            <div className="unit-stat-card" key={status.value}>
              <div className={`admintasks-stat-icon admintasks-stat-icon-${meta.tone}`}>
                <i className={`fa-solid ${meta.icon}`} aria-hidden="true"></i>
              </div>
              <div className="admintasks-stat-text">
                <span>{status.label}</span>
                <strong>{counts[status.value] || 0}</strong>
              </div>
            </div>
          );
        })}
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-list-check admintasks-list-icon" aria-hidden="true"></i>
              Task List
            </h5>
            <p>Search, filter, sort, view and update task records.</p>
          </div>

          <div className="unit-toolbar-actions">
            <InputGroup className="unit-search">
              <InputGroup.Text aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </InputGroup.Text>

              <Form.Control
                placeholder="Search title, category, status, assignee..."
                aria-label="Search title, category, status, assignee"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <Button className="unit-clear-btn" onClick={() => setSearch("")}>
                  Clear
                </Button>
              )}
            </InputGroup>

            <Form.Select
              className="unit-page-size"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size"
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
            >
              <option value="">All Units</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
            >
              <option value="">All Assignees</option>
              {users.map((userItem) => (
                <option key={userItem.id} value={userItem.id}>
                  {userItem.name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} rows
                </option>
              ))}
            </Form.Select>

            {(search || statusFilter || unitFilter || assigneeFilter) && (
              <Button className="unit-btn unit-btn-light" onClick={clearFilters}>
                Reset
              </Button>
            )}
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader">
              <Spinner animation="border" />
              <p>Loading tasks...</p>
            </div>
          ) : (
            <table className="table unit-table">
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>S.No.</th>

                  <th
                    className="sortable"
                    onClick={() => onSort("title")}
                  >
                    Task <SortIcon field="title" />
                  </th>

                  <th>Type</th>

                  <th>Category</th>

                  <th>Project</th>

                  <th>Staff</th>

                  <th
                    className="sortable"
                    onClick={() => onSort("assigned_to_name")}
                  >
                    Assigned <SortIcon field="assigned_to_name" />
                  </th>

                  <th
                    className="sortable"
                    onClick={() => onSort("status")}
                  >
                    Status <SortIcon field="status" />
                  </th>

                  <th
                    className="sortable"
                    onClick={() => onSort("created_at")}
                  >
                    Created <SortIcon field="created_at" />
                  </th>

                  <th className="text-end" style={{ width: "150px" }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {current.length > 0 ? (
                  current.map((task, index) => (
                    <tr key={task.id}>
                      <td>
                        <span className="unit-id">#{serialNumber(index)}</span>
                      </td>

                      <td>
                        <div className="unit-name-box">
                          <div className="unit-letter">
                            {taskDisplayTitle(task).charAt(0).toUpperCase() || "T"}
                          </div>

                          <div>
                            <strong>{taskDisplayTitle(task)}</strong>
                            <small>{task.description || "—"}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <Badge bg="light" text="dark" className="admintasks-type-badge">{task.task_type_name || "IT Support"}</Badge>
                        {task.attachment_count > 0 && (
                          <div>
                            <small className="text-muted admintasks-attachment-count">
                              <i className="fa-solid fa-paperclip" aria-hidden="true"></i> {task.attachment_count}
                            </small>
                          </div>
                        )}
                      </td>

                      <td>
                        {task.category_name || "-"}
                        {task.sub_category_name ? ` / ${task.sub_category_name}` : ""}
                      </td>

                      <td>
                        {task.project_name || "-"}
                        {task.project_module_name ? ` / ${task.project_module_name}` : ""}
                        {task.department_name && (
                          <div><small className="text-muted">Dept: {task.department_name}</small></div>
                        )}
                      </td>

                      <td>{task.requested_for_name || "-"}</td>

                      <td>
                        {task.assigned_to_name ? (
                          <div className="unit-name-box">
                            <div className="unit-letter">
                              {task.assigned_to_name?.charAt(0)?.toUpperCase()}
                            </div>

                            <div>
                              <strong>{task.assigned_to_name}</strong>
                              <small>Assignee</small>
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">Not assigned</span>
                        )}
                      </td>

                      <td>
                        <StatusBadge value={task.status} />
                      </td>

                      <td>
                        {task.created_at
                          ? new Date(task.created_at).toLocaleString()
                          : "-"}
                      </td>

                      <td>
                        <div className="unit-actions">
                          <Button
                            className="unit-icon-btn unit-edit"
                            title="View"
                            aria-label={`View ${taskDisplayTitle(task) || "task"}`}
                            onClick={() => openView(task.id)}
                          >
                            <i className="fa-solid fa-eye" aria-hidden="true"></i>
                          </Button>

                          <Button
                            className="unit-icon-btn unit-edit"
                            title="Edit"
                            aria-label={`Edit ${taskDisplayTitle(task) || "task"}`}
                            onClick={() => openEdit(task)}
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-list-check" aria-hidden="true"></i>
                        </div>
                        <h5>No tasks found</h5>
                        <p>Try another filter or create a new task.</p>

                        <Button
                          className="unit-btn unit-btn-primary"
                          onClick={openCreateModal}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                          Add Task
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing{" "}
            <strong>{sorted.length === 0 ? 0 : (page - 1) * pageSize + 1}</strong>{" "}
            to <strong>{Math.min(page * pageSize, sorted.length)}</strong> of{" "}
            <strong>{sorted.length}</strong> entries
          </div>

          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              />

              {compactPageItems([...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === page}
                  onClick={() => setPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              )), page)}

              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              />
            </Pagination>
          )}
        </div>
      </div>

      <Modal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        centered
        className="unit-modal"
        dialogClassName="admin-teal-modal"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton closeLabel="Close">
          <div className="d-flex align-items-center">
            <AssignmentOutlinedIcon className="modal-header-icon" aria-hidden="true" />
            <Modal.Title>Create Task</Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={createForm.title}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
              placeholder="Enter task title"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={createForm.description}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Enter task description"
            />
          </Form.Group>

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Unit <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={createForm.unit_id}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      unit_id: e.target.value,
                    }))
                  }
                >
                  <option value="">-- Select Unit --</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={createForm.category_id}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      category_id: e.target.value,
                    }))
                  }
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-12">
              <Form.Group>
                <Form.Label>
                  Subcategory <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={createForm.sub_category_id}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      sub_category_id: e.target.value,
                    }))
                  }
                >
                  <option value="">-- Select Subcategory --</option>
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {createForm.sub_category_id && (
              <div className="col-md-12">
                <Form.Group>
                  <Form.Label>Estimated TAT</Form.Label>
                  {subcategoryHasTat ? (
                    <Form.Control
                      readOnly
                      value={`${selectedSubcategory.tat_value} ${selectedSubcategory.tat_unit}`}
                    />
                  ) : (
                    <Form.Text className="text-muted d-block">
                      No Turnaround Time is configured for this subcategory yet — it will be created without a due date.
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            )}

            {/* Simplified backdated Task Date -- available to every
                user/role that can already open this Add Task modal at all;
                no permission, no feature flag. `max` blocks a future pick
                in the UI as a courtesy only -- the backend independently
                re-validates and rejects a future date regardless of what a
                crafted request sends. */}
            <div className="col-md-12">
              <Form.Group>
                <Form.Label>
                  Task Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  max={todayDateString()}
                  value={createForm.task_date}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      task_date: e.target.value,
                    }))
                  }
                  required
                />
              </Form.Group>
            </div>
          </div>

          <small className="text-muted d-block mt-3">
            Fields marked * are required.
          </small>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setShowCreate(false)}
          >
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={onCreate}
            disabled={
              !createForm.title ||
              !createForm.unit_id ||
              !createForm.category_id ||
              !createForm.sub_category_id ||
              !createForm.task_date
            }
          >
            Create Task
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        centered
        className="unit-modal"
        dialogClassName="admin-teal-modal"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton closeLabel="Close">
          <div className="d-flex align-items-center">
            <EditNoteOutlinedIcon className="modal-header-icon" aria-hidden="true" />
            <Modal.Title>Edit Task</Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="unit-card mb-3" style={{ boxShadow: "none" }}>
            <div className="p-3">
              <h6 className="mb-1">{taskDisplayTitle(editTask)}</h6>
              {editTask?.description && (
                <p className="text-muted mb-0">{editTask.description}</p>
              )}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                {/* IT Support complaint: Technicians + current owner only
                    (utils/complaintAssignee.js); other task types unchanged. */}
                <Form.Label htmlFor="admin-edit-assignee">{isComplaintTask(editTask) ? "Assign Technician" : "Assign To"}</Form.Label>
                <Form.Select
                  id="admin-edit-assignee"
                  value={editAssignedTo}
                  onChange={(e) => setEditAssignedTo(e.target.value)}
                >
                  <option value="">{isComplaintTask(editTask) ? "-- Select Technician --" : "-- Select User --"}</option>
                  {assigneeOptionsFor(editTask, users).map((userItem) => (
                    <option key={userItem.id} value={userItem.id}>
                      {assigneeOptionLabel(userItem)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  <option value="">-- Select Status --</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mt-3">
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              placeholder="Optional note saved as system remark"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setShowEdit(false)}
          >
            Cancel
          </Button>

          <Button className="unit-btn unit-btn-primary" onClick={saveEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}